<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { fade, fly } from 'svelte/transition';
  import { AppState } from '../../etc/stores';

  import ChevronRight from '../icons/ChevronRight.svelte';
  import Warn from '../icons/Warn.svelte';

  import FriendItem from './FriendItem.svelte';

  import { copyTextToClipboard, pluralizeI18n } from '../../etc/utils';
  import { Logger } from '../../services/logger';
  import { Container } from '../../services/container';
  import { Backend } from '../../services/backend';
  import Heading from '../typography/Heading.svelte';
  import Label from '../typography/Label.svelte';
  import Button from '../form/Button.svelte';
  import type { ICustomerReferralLink } from '../../services/referral-program';
  import Check from '../icons/Check.svelte';
  import CopyClipboard from '../icons/CopyClipboard.svelte';
  import { replaceVariables } from '../../i18n';

  const backend = Container.get(Backend);
  const logger = Container.get(Logger).context('Tracking.svelte') as Logger;

  const emit = createEventDispatcher();

  export let programType: string;
  export let referralLink: Partial<ICustomerReferralLink>;

  let collapsed = true;
  let coins = 0;
  let loading = false;
  let copied = false;
  let earnings = [];

  const t = (text: string, values?: Record<string, any>) => {
    return replaceVariables($_(`referral.tracking.${text}`), values);
  };

  const onClick = () => {
    collapsed = !collapsed;
    if (!collapsed) {
      emit('open');
    }
  };

  const loadList = async () => {
    try {
      loading = true;
      const response = await backend.getReferralEarnings();
      earnings = response.earnings;
      coins = response.totalCoins;
    } catch (e) {
      logger.error(e);
      earnings = [];
    } finally {
      loading = false;
    }
  };

  const copyLink = () => {
    copyTextToClipboard(referralLink.link);

    copied = true;
    setTimeout(() => {
      copied = false;
    }, 3800);
  };

  onMount(() => {
    loadList();
  });
</script>

<div
  style="background-color: {$AppState.theme.colors.referral.tracking
    .background}"
  class="tracking-wrapper"
>
  <div class="tracking-wrapper__collapsed" on:click={onClick}>
    <Heading type="h3" component="h3">{t('title')}</Heading>
    <div
      class="tracking-wrapper__arrow"
      class:tracking-wrapper__arrow-collapsed={collapsed}
    >
      <ChevronRight color={$AppState.theme.colors.typography} />
    </div>
  </div>
  {#if !collapsed}
    <div class="tracking-wrapper__content" transition:fly={{ y: -10 }}>
      {#if earnings.length}
        <div class="tracking-wrapper__content-header">
          <Label component="p" type="default-regular">{t('invites')}</Label>
          {#if coins}
            <Label component="p" type="default-semibold">
              {t(pluralizeI18n('total', coins), { coins })}
            </Label>
          {/if}
        </div>
        {#each earnings as item}
          <div class="tracking-wrapper__item">
            <FriendItem {item} />
          </div>
        {/each}
      {:else}
        <div class="tracking-wrapper__empty">
          <Warn />
          <Label
            class="tracking-wrapper__title"
            type="default-semibold"
            textAlign="center"
          >
            {t(`empty.title.${programType}`)}
          </Label>
          <Label
            class="tracking-wrapper__description"
            type="default-regular"
            textAlign="center"
          >
            {t(`empty.description`)}
          </Label>
          <div class="tracking-wrapper__button-container">
            {#if copied}
              <div in:fade={{ duration: 350 }}>
                <Button
                  schema="green"
                  on:click={copyLink}
                  label={t('empty.copied')}
                >
                  <div
                    class="tracking-wrapper__icon-gift-container"
                    slot="left-icon"
                  >
                    <Check stroke="#FFFFFF" />
                  </div>
                </Button>
              </div>
            {:else}
              <div>
                <Button on:click={copyLink} label={t('empty.button')}>
                  <div
                    class="tracking-wrapper__icon-gift-container"
                    slot="left-icon"
                  >
                    <CopyClipboard color={$AppState.theme.colors.textAboveBackground} />
                  </div>
                </Button>
              </div>
            {/if}
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">
  :global(.tracking-wrapper__title) {
    margin: 16px 0 8px !important;
    line-height: 16px !important;
  }

  :global(.tracking-wrapper__description) {
    margin-bottom: 24px !important;
    max-width: 312px;
    line-height: 24px !important;
  }

  .tracking-wrapper {
    margin-top: 40px;

    &__collapsed {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 32px 24px;
      cursor: pointer;
    }
    &__arrow {
      transform: rotate(-90deg);
      max-height: 24px;
      transition: transform 0.1s;
    }
    &__arrow-collapsed {
      transform: rotate(90deg);
    }
    &__content {
      padding: 0 24px 32px;
    }
    &__content-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 4px;
    }

    &__item {
      margin-top: 12px;
    }

    &__empty {
      align-items: center;
      justify-content: center;
      display: flex;
      flex-direction: column;
    }

    &__icon-gift-container {
      width: 30px;
      height: 24px;
      transform: translateY(-1px) translateX(-2px);
    }

    &__button-container {
      width: 100%;
      max-height: 48px;
    }
  }
</style>
